/**
 * ID Generator Utilities
 *
 * Provides functions for generating unique identifiers used throughout the agent system.
 * All IDs are designed to be:
 * - Short (for token efficiency)
 * - Prefixed (for type identification and routing)
 * - Unique (within their scope)
 */
// ═══════════════════════════════════════════════════════════════════════════
// RANDOM STRING GENERATION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Character set for generating random IDs
 * Lowercase alphanumeric for readability and URL safety
 */
const ID_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';
/**
 * Generate a random string of specified length
 *
 * @param length - Number of characters (default: 8)
 * @returns Random alphanumeric string
 */
export function randomString(length = 8) {
    let result = '';
    for (let i = 0; i < length; i++) {
        result += ID_CHARS[Math.floor(Math.random() * ID_CHARS.length)];
    }
    return result;
}
// ═══════════════════════════════════════════════════════════════════════════
// PREFIXED ID GENERATION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Generate a prefixed ID
 *
 * @param prefix - The prefix to use (e.g., 'task', 'conv', 'as')
 * @param length - Length of the random portion (default: 8)
 * @returns Prefixed ID like "task_x7f9k2ab"
 *
 * @example
 * generateId('task')      // "task_x7f9k2ab"
 * generateId('conv', 12)  // "conv_x7f9k2ab3def"
 */
export function generateId(prefix, length = 8) {
    return `${prefix}_${randomString(length)}`;
}
// ═══════════════════════════════════════════════════════════════════════════
// SPECIALIZED ID GENERATORS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Generate a task ID for agent registry
 * Format: task_{random}
 */
export function generateTaskId() {
    return generateId('task');
}
/**
 * Generate a conversation ID
 * Format: {userUuid}__{uniqueIdentifier}
 *
 * @param userUuid - The user's UUID (from user._id)
 */
export function generateConversationId(userUuid) {
    if (!userUuid) {
        throw new Error('userUuid is required');
    }
    const uniqueId = randomString(8);
    return `${userUuid}__${uniqueId}`;
}
/**
 * Generate a thread ID for an agent type
 * Format: {agentType}_thread_{random}
 *
 * @param agentType - The agent type (e.g., 'alphasense', 'image_search')
 * @returns Thread ID like "alphasense_thread_x7f9k2ab"
 */
export function generateThreadId(agentType) {
    return `${agentType}_thread_${randomString(8)}`;
}
/**
 * Generate a deck ID
 * Format: deck_{random}
 */
export function generatedocumentId() {
    return generateId('deck');
}
/**
 * Generate a persisted conversation ID
 * Format: pconv_{random12}
 * Longer random portion for uniqueness across sessions
 */
export function generatePersistedConversationId() {
    return generateId('pconv', 12);
}
/**
 * Global set of IDs currently in use
 * Used to avoid collisions when generating new IDs
 *
 * NOTE: In the future, we should sync this with Python backend's used IDs
 * to ensure cross-service uniqueness. TODO: Implement ID sync endpoint.
 */
const usedIds = new Set();
/**
 * Register an ID as being in use
 * Call this when storing content or receiving IDs from external sources
 *
 * @param id - The ID to register
 */
export function registerUsedId(id) {
    usedIds.add(id);
}
/**
 * Register multiple IDs as being in use
 *
 * @param ids - Array of IDs to register
 */
export function registerUsedIds(ids) {
    for (const id of ids) {
        usedIds.add(id);
    }
}
/**
 * Unregister an ID (e.g., when content is deleted)
 *
 * @param id - The ID to unregister
 */
export function unregisterUsedId(id) {
    usedIds.delete(id);
}
/**
 * Clear all registered used IDs
 * Use with caution - typically only for testing or session reset
 */
export function clearUsedIds() {
    usedIds.clear();
}
/**
 * Get all currently used IDs
 *
 * @returns Set of used IDs
 */
export function getUsedIds() {
    return usedIds;
}
/**
 * Check if an ID is already in use
 *
 * @param id - The ID to check
 * @returns true if the ID is in use
 */
export function isIdUsed(id) {
    return usedIds.has(id);
}
/**
 * Generate a context ID for content storage
 * Format: {prefix}_{random}
 *
 * Ensures uniqueness by checking against:
 * 1. Global usedIds set
 * 2. Optional excludeIds parameter
 *
 * Recursively regenerates if collision detected (extremely rare with 6-char random).
 *
 * @param prefix - The content type prefix
 * @param length - Length of random portion (default: 6 for brevity)
 * @param excludeIds - Optional additional IDs to avoid (e.g., session-specific)
 * @returns Context ID like "as_x7f9k2"
 *
 * @example
 * generateContextID('as')                    // "as_x7f9k2"
 * generateContextID('logo', 6, ['logo_abc']) // "logo_def456" (not 'logo_abc')
 */
export function generateContextID(prefix, length = 6, excludeIds) {
    const excludeSet = excludeIds
        ? (excludeIds instanceof Set ? excludeIds : new Set(excludeIds))
        : new Set();
    // Generate a candidate ID
    const id = `${prefix}_${randomString(length)}`;
    // Check against both global used IDs and exclude list
    if (!usedIds.has(id) && !excludeSet.has(id)) {
        // Auto-register the ID
        usedIds.add(id);
        return id;
    }
    // Collision detected - recurse to regenerate
    return generateContextID(prefix, length, excludeIds);
}
// ═══════════════════════════════════════════════════════════════════════════
// COMPOSITE ID DETECTION
// ═══════════════════════════════════════════════════════════════════════════
/** Separator for composite IDs - using | because it never appears in natural identifiers */
const COMPOSITE_ID_SEPARATOR = '|';
/**
 * Check if a string is a composite ID (contains deck reference with | separator)
 *
 * Composite IDs have format: {deckRef}|{contentId}|{suffix?}
 * Examples:
 * - "d0|slide_257" - slide in deck 0
 * - "deck_abc123|slide_257|shape_42" - shape in a specific deck
 * - "CURRENT|layout_123" - layout in current deck
 *
 * @param id - String to check
 * @returns True if it looks like a composite ID
 */
export function isCompositeId(id) {
    if (!id || typeof id !== 'string') {
        return false;
    }
    // Check if it matches the pattern of having a | separator
    // and starts with a deck reference pattern (d\d+, CURRENT, or deck_)
    const hasSeparator = id.includes(COMPOSITE_ID_SEPARATOR);
    const startsWithDeckRef = /^(d\d+|CURRENT|deck_)/i.test(id);
    return hasSeparator && startsWithDeckRef;
}
/**
 * Extract the content type prefix from a composite ID
 *
 * For composite IDs like "deck_abc|slide_257|shape_42", extracts "slide" from the second segment.
 *
 * @param compositeId - The composite ID to parse
 * @returns The content type prefix (e.g., "slide", "layout", "shape") or null
 */
function getCompositeContentPrefix(compositeId) {
    const parts = compositeId.split(COMPOSITE_ID_SEPARATOR);
    if (parts.length < 2)
        return null;
    const contentId = parts[1]; // e.g., "slide_257"
    const underscoreIndex = contentId.indexOf('_');
    if (underscoreIndex === -1)
        return null;
    return contentId.slice(0, underscoreIndex); // e.g., "slide"
}
// ═══════════════════════════════════════════════════════════════════════════
// ID PARSING
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Parse a prefixed ID to extract its components
 *
 * @param id - The prefixed ID to parse
 * @returns Object with prefix and suffix, or null if invalid format
 *
 * @example
 * parseId('task_x7f9k2ab')  // { prefix: 'task', suffix: 'x7f9k2ab' }
 * parseId('invalid')        // null
 */
export function parseId(id) {
    const underscoreIndex = id.indexOf('_');
    if (underscoreIndex === -1) {
        return null;
    }
    return {
        prefix: id.slice(0, underscoreIndex),
        suffix: id.slice(underscoreIndex + 1),
    };
}
/**
 * Extract the prefix from a prefixed ID
 *
 * @param id - The prefixed ID
 * @returns The prefix, or null if invalid format
 */
export function getIdPrefix(id) {
    const parsed = parseId(id);
    return parsed?.prefix ?? null;
}
/**
 * Check if an ID has a specific prefix
 *
 * @param id - The ID to check
 * @param prefix - The expected prefix
 * @returns true if the ID starts with the prefix followed by underscore
 */
export function hasPrefix(id, prefix) {
    return id.startsWith(`${prefix}_`);
}
/**
 * Check if a string is a valid context ID with a known prefix
 *
 * Supports two formats:
 * 1. Simple prefixed IDs: "logo_abc123", "slide_257"
 * 2. Composite IDs: "deck_abc|slide_257|shape_42"
 *
 * @param id - The string to check
 * @returns true if it's a valid context ID format
 */
export function isValidContextID(id) {
    // File-scoped Excel read IDs used by ExternalFileSearchAgent
    // Example: f0|excelread_content|Sheet1!A1:B10
    if (/^(f\d+|file_\d+)\|excelread_[^|]+\|.+$/.test(id)) {
        return true;
    }
    // Composite IDs are valid (e.g., "deck_abc|slide_257")
    if (isCompositeId(id)) {
        return true;
    }
    const parsed = parseId(id);
    if (!parsed)
        return false;
    const validPrefixes = [
        'as', 'logo', 'color', 'wikimedia', 'meta',
        'slide', 'component', 'layout', 'asset', 'blueprint',
        'page', 'excelread', 'excel', 'artifact'
    ];
    return validPrefixes.includes(parsed.prefix);
}
/**
 * Extract the context ID prefix as a typed value
 *
 * Supports two formats:
 * 1. Simple prefixed IDs: "logo_abc123" → "logo"
 * 2. Composite IDs: "deck_abc|slide_257" → "slide" (extracted from content segment)
 *
 * @param id - The context ID
 * @returns The prefix as ContextIDPrefix, or null if invalid
 */
export function getContextIDPrefix(id) {
    const validPrefixes = [
        'as', 'logo', 'color', 'wikimedia', 'meta',
        'slide', 'component', 'layout', 'asset', 'blueprint',
        'page', 'excelread', 'excel', 'artifact'
    ];
    // File-scoped Excel read IDs used by ExternalFileSearchAgent
    // Example: f0|excelread_content|Sheet1!A1:B10
    if (/^(f\d+|file_\d+)\|excelread_[^|]+\|.+$/.test(id)) {
        return 'excelread';
    }
    // Handle composite IDs (e.g., "deck_abc|slide_257" → extract "slide")
    if (isCompositeId(id)) {
        const contentPrefix = getCompositeContentPrefix(id);
        if (contentPrefix && validPrefixes.includes(contentPrefix)) {
            return contentPrefix;
        }
        return null;
    }
    // Handle simple prefixed IDs (e.g., "logo_abc123")
    const prefix = getIdPrefix(id);
    if (!prefix)
        return null;
    return validPrefixes.includes(prefix)
        ? prefix
        : null;
}
